import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Download, Calendar, Info, Sparkles, AlertTriangle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DateRangePreset({ label, onClick, isActive }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm rounded-lg transition-all ${
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
      }`}
      data-testid={`preset-${label.toLowerCase().replace(' ', '-')}`}
    >
      {label}
    </button>
  );
}

export default function HealthcareExport() {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const today = new Date();
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29); // 30 days inclusive of today
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [includeAI, setIncludeAI] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activePreset, setActivePreset] = useState('30 Days');

  const setPreset = (days, label) => {
    const end = new Date();
    const start = new Date();
    // Subtract days-1 to get exactly 'days' days in range (inclusive of both start and end)
    start.setDate(end.getDate() - (days - 1));
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setActivePreset(label);
  };

  const handleDateChange = (type, value) => {
    if (type === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    setActivePreset(null);
  };

  const calculateDaysInRange = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleExport = async () => {
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/export/comprehensive-pdf`,
        {
          start_date: startDate,
          end_date: endDate,
          include_ai_analysis: includeAI
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `health_report_${startDate}_to_${endDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Healthcare report downloaded!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleQuickExport = async () => {
    setGenerating(true);
    try {
      const response = await axios.get(`${API}/export/healthcare-pdf?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quick_health_summary_${today.toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Quick summary downloaded!');
    } catch (err) {
      toast.error('Failed to generate quick summary');
    } finally {
      setGenerating(false);
    }
  };

  const daysInRange = calculateDaysInRange();

  return (
    <div className="min-h-screen pb-safe" data-testid="healthcare-export-page">
      <div className="max-w-lg mx-auto px-6 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
        </div>

        <div className="mt-6 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">Healthcare Export</h1>
          <p className="mt-2 text-muted-foreground">Generate detailed reports for your healthcare provider</p>
        </div>

        {/* Quick Export */}
        <div className="mt-6 card-soft bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-xl shadow-sm">
              <FileText className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">Quick Summary</h3>
              <p className="text-sm text-muted-foreground mt-1">Last 30 days, basic overview</p>
              <Button
                onClick={handleQuickExport}
                disabled={generating}
                variant="outline"
                size="sm"
                className="mt-3"
                data-testid="quick-export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {generating ? 'Generating...' : 'Download Quick PDF'}
              </Button>
            </div>
          </div>
        </div>

        {/* Comprehensive Export Section */}
        <div className="mt-6">
          <h2 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-500" />
            Comprehensive Report
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Custom date range with detailed analytics</p>
        </div>

        {/* Date Range Presets */}
        <div className="mt-4 flex flex-wrap gap-2">
          <DateRangePreset label="7 Days" onClick={() => setPreset(7, '7 Days')} isActive={activePreset === '7 Days'} />
          <DateRangePreset label="30 Days" onClick={() => setPreset(30, '30 Days')} isActive={activePreset === '30 Days'} />
          <DateRangePreset label="60 Days" onClick={() => setPreset(60, '60 Days')} isActive={activePreset === '60 Days'} />
          <DateRangePreset label="90 Days" onClick={() => setPreset(90, '90 Days')} isActive={activePreset === '90 Days'} />
          <DateRangePreset label="6 Months" onClick={() => setPreset(180, '6 Months')} isActive={activePreset === '6 Months'} />
          <DateRangePreset label="1 Year" onClick={() => setPreset(365, '1 Year')} isActive={activePreset === '1 Year'} />
        </div>

        {/* Custom Date Range */}
        <div className="mt-4 card-soft">
          <p className="text-sm font-medium text-foreground mb-3">Custom Date Range</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange('start', e.target.value)}
                max={endDate}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg text-sm bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange('end', e.target.value)}
                min={startDate}
                max={today.toISOString().split('T')[0]}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg text-sm bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="end-date-input"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Selected range: <span className="font-medium text-foreground">{daysInRange} days</span>
          </p>
        </div>

        {/* AI Analysis Toggle */}
        <div className="mt-4 card-soft">
          <div className="flex items-start gap-3">
            <div className="pt-0.5">
              <input
                type="checkbox"
                id="includeAI"
                checked={includeAI}
                onChange={(e) => setIncludeAI(e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                data-testid="include-ai-checkbox"
              />
            </div>
            <label htmlFor="includeAI" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                <span className="font-medium text-foreground text-sm">Include AI Analysis</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                AI-generated pattern analysis will be clearly marked and separated from your actual data
              </p>
            </label>
          </div>
        </div>

        {/* Report Contents Preview */}
        <div className="mt-6 card-soft bg-slate-50 border-slate-200">
          <h3 className="font-medium text-foreground text-sm flex items-center gap-2">
            <Info className="h-4 w-4 text-slate-500" />
            What's Included
          </h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Symptom Severity:</strong> Daily scores, averages, highest recorded</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Frequency:</strong> Episodes count, days reported per symptom</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Pain Location:</strong> Body parts with severity breakdown</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Functional Impact:</strong> Mobility, work, daily activities</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Flare Days:</strong> Count, percentage, dates</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Medication:</strong> Adherence, timing patterns</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Trends:</strong> Visual charts + dated entry table with notes</span>
            </li>
          </ul>
          
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Data Clarity:</strong> The report distinguishes between days without entries and days explicitly marked as symptom-free. Summaries are calculated only from recorded data.
              </p>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <Button
          onClick={handleExport}
          disabled={generating}
          className="w-full mt-6 btn-primary"
          data-testid="generate-comprehensive-btn"
        >
          {generating ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              Generating Report...
            </>
          ) : (
            <>
              <Download className="h-5 w-5 mr-2" />
              Generate Comprehensive Report
            </>
          )}
        </Button>

        <p className="text-center text-xs text-muted-foreground mt-3">
          PDF will download automatically when ready
        </p>

        <div className="h-8" />
      </div>
    </div>
  );
}
